import { useI18n } from '../i18n';

const INTL: Record<string, string> = { uk: 'uk-UA', en: 'en-GB', ro: 'ro-MD', ru: 'ru-RU', pl: 'pl-PL' };

export function intlLocale(l: string): string {
  return INTL[l] ?? l;
}

function safeDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string | null | undefined, l: string, withTime = true): string {
  const d = safeDate(iso);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(intlLocale(l), {
      day: 'numeric',
      month: 'short',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(d);
  } catch {
    return d.toISOString().slice(0, withTime ? 16 : 10).replace('T', ' ');
  }
}

/** Country code → localized name ("MD" → "Moldova" / "Молдова"); unknown codes stay as they are. */
export function countryName(code: string | null | undefined, l: string): string {
  if (!code) return '';
  try {
    return new Intl.DisplayNames([intlLocale(l)], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function isToday(iso: string | null | undefined, now = new Date()): boolean {
  const d = safeDate(iso);
  return !!d && d.toDateString() === now.toDateString();
}

/** Hook-friendly helpers bound to the current language. */
export function useFormat() {
  const locale = useI18n((s) => s.locale);
  return {
    locale,
    date: (iso: string | null | undefined, withTime = true) => formatDate(iso, locale, withTime),
    country: (code: string | null | undefined) => countryName(code, locale),
  };
}

/** Countries partners usually work with (the case form offers these first). */
export const COMMON_COUNTRIES = ['MD', 'RO', 'UA', 'IT', 'DE', 'PT', 'PL', 'ES', 'FR', 'GB', 'IE', 'CZ', 'AT', 'BE', 'NL', 'IL', 'TR', 'RU', 'BY', 'US', 'CA'];

/**
 * Names from the DB are often `{uk, en, ro, …}` (jsonb): pick the current language; for Cyrillic UIs (uk/ru) fall
 * back to uk, then en; for Latin-script UIs (ro, en, pl, …) fall back to en first, so a Romanian screen never shows
 * a Ukrainian name when an English one exists.
 */
export function localName(name: unknown, l: string): string {
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') {
    const o = name as Record<string, unknown>;
    const pick = (k: string) => (typeof o[k] === 'string' && o[k] ? (o[k] as string) : undefined);
    const order = l === 'uk' || l === 'ru' ? [l, 'uk', 'en'] : [l, 'en', 'uk'];
    const v = order.map(pick).find(Boolean) ?? Object.values(o).find((x) => typeof x === 'string');
    return typeof v === 'string' ? v : '';
  }
  return '';
}
